import React, { useState } from 'react';
import EmailComposerModal from '../outreach/EmailComposerModal';
import './ProspectDetailModal.css';

const ProspectDetailModal = ({ isOpen, onClose, prospect }) => {
  const [copiedEmail, setCopiedEmail] = useState(null);
  const [isEmailComposerOpen, setIsEmailComposerOpen] = useState(false);
  if (!isOpen || !prospect) return null;

  const parseJsonSafe = (value, fallback) => {
    if (typeof value !== 'string') return value ?? fallback;
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  const normalizeVisibleValues = (value) => {
    const isMaskedValue = (val) => {
      const text = String(val || '').trim();
      if (!text) return true;
      return /(\.\.\.|…|\*{2,}|x{2,}|X{2,})/.test(text);
    };
    const raw = parseJsonSafe(value, value);
    const list = Array.isArray(raw) ? raw : [raw];
    const cleaned = list
      .map((item) => {
        if (typeof item === 'object' && item !== null) {
          return String(
            item.email || item.number || item.phone || item.value || item.raw || ''
          ).trim();
        }
        return String(item || '').trim();
      })
      .filter(Boolean)
      .filter((item) => !isMaskedValue(item));
    return cleaned;
  };

  const emails = normalizeVisibleValues(prospect.email);
  const phones = normalizeVisibleValues(
    prospect.phone_number || prospect.phone_numbers || prospect.phone
  );

  const normalizeDisplayValue = (value) => {
    if (value == null) return '';
    const parsed = parseJsonSafe(value, value);

    if (Array.isArray(parsed)) {
      const flat = parsed
        .map((item) => {
          if (typeof item === 'object' && item !== null) {
            return String(
              item.value || item.name || item.label || item.title || item.department || ''
            ).trim();
          }
          return String(item || '').trim();
        })
        .filter(Boolean);
      return flat.join(', ');
    }

    if (typeof parsed === 'object') {
      return String(
        parsed.value || parsed.name || parsed.label || parsed.title || parsed.department || ''
      ).trim();
    }

    return String(parsed).trim();
  };

  const formatRangeValue = (value) => {
    if (value == null) return '';
    const parsed = parseJsonSafe(value, value);

    if (typeof parsed === 'object' && parsed !== null) {
      if (parsed.min != null || parsed.max != null) {
        const min = parsed.min != null ? String(parsed.min) : '';
        const max = parsed.max != null ? String(parsed.max) : '';
        if (min && max) return `${min}-${max}`;
        if (min) return `${min}+`;
        if (max) return max;
      }

      if (parsed.raw != null) {
        return String(parsed.raw).trim();
      }

      return '';
    }

    return String(parsed).trim();
  };

  const formatLocationValue = (value) => {
    if (value == null) return '';
    const parsed = parseJsonSafe(value, value);
    if (typeof parsed === 'object' && parsed !== null) {
      return String(
        parsed.country || parsed.city || parsed.state || parsed.raw_location || ''
      ).trim();
    }
    return String(parsed).trim();
  };

  // Extract from company_details JSON if available
  const companyDetails = parseJsonSafe(prospect.company_details, {}) || {};
  const companyName =
    prospect.company_name ||
    companyDetails.companyName ||
    companyDetails.name ||
    companyDetails.company?.name ||
    'Unknown Company';

  const industry = normalizeDisplayValue(
    prospect.company_industry ||
      prospect.industry ||
      companyDetails.mainIndustry ||
      companyDetails.industry ||
      ''
  );
  const companySize = formatRangeValue(prospect.company_size || companyDetails.employees || '');
  const revenue = formatRangeValue(prospect.company_revenue || companyDetails.revenue || '');
  const companyLocation = formatLocationValue(
    prospect.company_location ||
      companyDetails.location ||
      companyDetails.country ||
      prospect.location ||
      ''
  );
  const companyLogo =
    companyDetails.logoUrl ||
    companyDetails.logo_url ||
    companyDetails.logo ||
    companyDetails.company?.logoUrl ||
    companyDetails.company?.logo_url ||
    null;

  return (
    <div className="pd-modal-overlay" onClick={onClose}>
      <div className="pd-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="pd-header">
          <div className="pd-header-top">
            <div className="pd-header-info">
              <h2>{prospect.name}</h2>
              <p className="pd-job-title">{prospect.job_title}</p>
              {prospect.linkedin_url && (
                <a
                  href={prospect.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pd-linkedin-link"
                >
                  <i className="fab fa-linkedin"></i> View Linkedln
                </a>
              )}
            </div>
            <button className="pd-close-btn" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div className="pd-body">
          <div className="pd-section">
            <h3 className="pd-section-title">
              <i className="fas fa-id-card"></i> Contact Information
            </h3>
            <div className="pd-info-cards">
              <div className="pd-info-card pd-span-2">
                <span className="pd-label">
                  <i className="fas fa-envelope"></i> Emails
                </span>
                <div className="pd-email-list">
                  {emails.length > 0 ? (
                    emails.map((email, idx) => (
                      <div key={idx} className="pd-email-item">
                        <span className="pd-value pd-highlight">{email}</span>
                        <div className="pd-email-actions">
                          <div className="pd-action-group">
                            {copiedEmail === email && (
                              <span className="pd-copy-feedback">Copied!</span>
                            )}
                            <button
                              className="pd-icon-action"
                              onClick={() => {
                                navigator.clipboard.writeText(email);
                                setCopiedEmail(email);
                                setTimeout(() => setCopiedEmail(null), 2000);
                              }}
                              title="Copy Email"
                            >
                              <i
                                className={copiedEmail === email ? 'fas fa-check' : 'fas fa-copy'}
                              ></i>
                            </button>
                          </div>
                          <button
                            className="pd-icon-action pd-primary-action"
                            onClick={() => setIsEmailComposerOpen(true)}
                            title="Send Outreach Email"
                          >
                            <i className="fas fa-paper-plane"></i>
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <span className="ar-not-available">-</span>
                  )}
                </div>
              </div>
              <EmailComposerModal
                isOpen={isEmailComposerOpen}
                onClose={() => setIsEmailComposerOpen(false)}
                selectedProspects={[prospect]}
                onSent={(data) => {
                  // Optional: show a toast or message
                }}
              />
              <div className="pd-info-card">
                <span className="pd-label">
                  <i className="fas fa-phone-alt"></i> Phones
                </span>
                <div className="pd-email-list">
                  {phones.length > 0 ? (
                    phones.map((phone, idx) => (
                      <div key={idx} className="pd-email-item">
                        <span className="pd-value">{phone}</span>
                      </div>
                    ))
                  ) : (
                    <span className="ar-not-available">-</span>
                  )}
                </div>
              </div>
              <div className="pd-info-card">
                <span className="pd-label">
                  <i className="fas fa-user-tie"></i> Seniority
                </span>
                <span className="pd-value">
                  {normalizeDisplayValue(prospect.seniority || prospect.management_level) || (
                    <span className="ar-not-available">-</span>
                  )}
                </span>
              </div>
              <div className="pd-info-card">
                <span className="pd-label">
                  <i className="fas fa-briefcase"></i> Department
                </span>
                <span className="pd-value">
                  {normalizeDisplayValue(prospect.department || prospect.departments) || (
                    <span className="ar-not-available">-</span>
                  )}
                </span>
              </div>
              <div className="pd-info-card">
                <span className="pd-label">
                  <i className="fas fa-map-marker-alt"></i> Location
                </span>
                <span className="pd-value">
                  {typeof prospect.location === 'object'
                    ? prospect.location.country ||
                      prospect.location.city ||
                      JSON.stringify(prospect.location)
                    : prospect.location || <span className="ar-not-available">-</span>}
                </span>
              </div>
            </div>
          </div>

          <div className="pd-section">
            <h3 className="pd-section-title">
              <i className="fas fa-building"></i> Company Information
            </h3>
            <div className="pd-company-header">
              {companyLogo && (
                <img src={companyLogo} alt={companyName} className="pd-company-logo" />
              )}
              <div className="pd-company-title-wrap">
                <span className="pd-label">
                  <i className="fas fa-building"></i> Company Name
                </span>
                <span className="pd-value pd-company-name-large">{companyName}</span>
              </div>
            </div>
            <div className="pd-info-cards">
              <div className="pd-info-card pd-span-2">
                <span className="pd-label">
                  <i className="fas fa-industry"></i> Industry
                </span>
                <span className="pd-value">
                  {industry || <span className="ar-not-available">-</span>}
                </span>
              </div>
              <div className="pd-info-card">
                <span className="pd-label">
                  <i className="fas fa-money-bill-wave"></i> Revenue
                </span>
                <span className="pd-value">
                  {revenue || <span className="ar-not-available">-</span>}
                </span>
              </div>
              <div className="pd-info-card">
                <span className="pd-label">
                  <i className="fas fa-users"></i> Size
                </span>
                <span className="pd-value">
                  {companySize || <span className="ar-not-available">-</span>}
                </span>
              </div>
              <div className="pd-info-card pd-span-2">
                <span className="pd-label">
                  <i className="fas fa-globe"></i> Location
                </span>
                <span className="pd-value">
                  {companyLocation || <span className="ar-not-available">-</span>}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProspectDetailModal;
